/* eslint-disable */
/**
 * Admin → City Guides panel.
 *
 * Lists 13 cities + their full_guide health, lets admin regenerate any
 * guide via Claude Sonnet 4, toggle published flag, edit individual sections,
 * run Spanish translation, and link to the portal preview.
 */
import React, { useEffect, useState } from 'react';
import { Globe, RefreshCw, CheckCircle2, AlertCircle, Eye, EyeOff, ExternalLink, Edit3, Languages, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminCityGuidesPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/city-guides/full-list`, { credentials: 'include' });
      const d = await r.json();
      setRows(d.cities || []);
    } catch {
      setMsg({ kind: 'error', text: 'Failed to load city guides' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const regen = async (slug) => {
    if (!window.confirm(`Regenerate the full guide for ${slug}? This calls Claude Sonnet 4 (3 chunks).`)) return;
    setBusy(slug); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/city-guides/${slug}/full-regenerate`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (r.ok) { setMsg({ kind: 'success', text: `Regenerated ${slug} (${d.section_count} sections)` }); await fetchRows(); }
      else      { setMsg({ kind: 'error', text: `Failed: ${d.detail}` }); }
    } catch (e) { setMsg({ kind: 'error', text: e.message }); }
    finally    { setBusy(null); }
  };

  const togglePublish = async (slug, currentlyPublished) => {
    setBusy(slug); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/city-guides/${slug}/full-publish?published=${!currentlyPublished}`, { method: 'POST', credentials: 'include' });
      if (r.ok) await fetchRows();
      else { const d = await r.json(); setMsg({ kind: 'error', text: d.detail }); }
    } finally { setBusy(null); }
  };

  const translateEs = async (slug) => {
    if (!window.confirm(`Translate ${slug} into Spanish? Calls Claude once per section (~11 calls, ~3 min).`)) return;
    setBusy(slug); setMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/city-guides/${slug}/translate-full`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (r.ok) setMsg({ kind: 'success', text: `Translated ${slug} (${d.section_count} ES sections)` });
      else      setMsg({ kind: 'error', text: `Failed: ${d.detail}` });
    } catch (e) { setMsg({ kind: 'error', text: e.message }); }
    finally    { setBusy(null); }
  };

  const openEditor = async (slug) => {
    const r = await fetch(`${API}/api/city-guides/${slug}/full`, { credentials: 'include' });
    if (!r.ok) { setMsg({ kind: 'error', text: 'Failed to load sections' }); return; }
    const d = await r.json();
    setEditing({ slug, name: d.name, sections: d.full_guide?.sections || [] });
  };

  const saveSection = async (sectionId, payload) => {
    const r = await fetch(`${API}/api/admin/city-guides/${editing.slug}/full-section/${sectionId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) { setMsg({ kind: 'error', text: d.detail || 'save failed' }); return false; }
    setMsg({ kind: 'success', text: `Section "${sectionId}" saved` });
    setEditing((prev) => prev && ({
      ...prev,
      sections: prev.sections.map((s) => s.id === sectionId ? { ...s, ...payload } : s),
    }));
    return true;
  };

  return (
    <div className="space-y-4" data-testid="admin-city-guides-panel">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl text-[#0A1F2E] flex items-center gap-2"><Globe className="w-5 h-5 text-[#2CA8A0]" /> City Guides</h2>
          <p className="text-sm text-[#5A6B73]">Manage the rich Claude-generated guide for each city. Free tier locks them; Guided gets 3 free; others $4.99.</p>
        </div>
        <button onClick={fetchRows} className="text-sm text-[#1A5C6E] hover:text-[#2CA8A0] flex items-center gap-1" data-testid="admin-city-guides-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      {msg && (
        <div className={`text-sm px-3 py-2 rounded ${msg.kind === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>{msg.text}</div>
      )}

      {loading ? (
        <p className="text-[#5A6B73]">Loading…</p>
      ) : (
        <div className="bg-white border border-[#F4ECD8] overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-[#F9F6EF]">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-semibold text-[#5A6B73] uppercase">City</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-[#5A6B73] uppercase">Status</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-[#5A6B73] uppercase">Sections</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-[#5A6B73] uppercase">Scorecard</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-[#5A6B73] uppercase">Last gen</th>
                <th className="text-right px-3 py-2 text-xs font-semibold text-[#5A6B73] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const ok = r.has_full_guide;
                const isBusy = busy === r.slug;
                return (
                  <tr key={r.slug} className="border-t border-[#F4ECD8]">
                    <td className="px-3 py-2">
                      <p className="text-sm font-medium text-[#0A1F2E]">{r.name}</p>
                      <p className="text-xs text-[#5A6B73]">{r.state}</p>
                    </td>
                    <td className="px-3 py-2">
                      {ok ? (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />ready</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" />empty</span>
                      )}
                      {ok && !r.published && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">unpublished</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-[#1A5C6E]">{r.section_count}</td>
                    <td className="px-3 py-2 text-sm text-[#1A5C6E]">{r.scorecard_filled ? '✓' : '-'}</td>
                    <td className="px-3 py-2 text-xs text-[#1A5C6E]">{r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : '-'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex flex-wrap gap-1 justify-end">
                        <button onClick={() => openEditor(r.slug)} disabled={!ok || isBusy} className="text-xs px-2 py-1 bg-[#E8A830]/15 text-[#0A1F2E] rounded hover:bg-[#E8A830]/30 disabled:opacity-40 inline-flex items-center gap-1" data-testid={`admin-cg-edit-${r.slug}`}><Edit3 className="w-3 h-3" /> Edit</button>
                        <button onClick={() => translateEs(r.slug)} disabled={!ok || isBusy} className="text-xs px-2 py-1 bg-[#1A5C6E]/10 text-[#1A5C6E] rounded hover:bg-[#1A5C6E]/20 disabled:opacity-40 inline-flex items-center gap-1" data-testid={`admin-cg-translate-${r.slug}`}><Languages className="w-3 h-3" /> ES</button>
                        <button onClick={() => regen(r.slug)} disabled={isBusy} className="text-xs px-2 py-1 bg-[#2CA8A0]/10 text-[#2CA8A0] rounded hover:bg-[#2CA8A0]/20 disabled:opacity-40" data-testid={`admin-cg-regen-${r.slug}`}>{isBusy ? 'Working…' : 'Regenerate'}</button>
                        <button onClick={() => togglePublish(r.slug, r.published)} disabled={isBusy || !ok} className="text-xs px-2 py-1 bg-[#F4ECD8] text-[#0A1F2E] rounded hover:bg-[#EFE6CF] disabled:opacity-40 inline-flex items-center gap-1" data-testid={`admin-cg-toggle-${r.slug}`}>{r.published ? <><EyeOff className="w-3 h-3" /> Unpublish</> : <><Eye className="w-3 h-3" /> Publish</>}</button>
                        <a href={`/portal/city-guides/${r.slug}`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 bg-[#0A1F2E] text-white rounded hover:bg-[#1A5C6E] inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View</a>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-[#5A6B73]">No cities found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SectionEditorModal editing={editing} onClose={() => setEditing(null)} onSave={saveSection} />
      )}
    </div>
  );
};

const SectionEditorModal = ({ editing, onClose, onSave }) => {
  const [activeId, setActiveId] = useState(editing.sections[0]?.id);
  const section = editing.sections.find((s) => s.id === activeId) || editing.sections[0];
  const [intro, setIntro] = useState(section?.intro || '');
  const [narrative, setNarrative] = useState(section?.narrative || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setIntro(section?.intro || '');
    setNarrative(section?.narrative || '');
  }, [activeId, section?.intro, section?.narrative]);

  const save = async () => {
    setSaving(true);
    const payload = {};
    if (intro !== (section.intro || '')) payload.intro = intro;
    if (narrative !== (section.narrative || '')) payload.narrative = narrative;
    if (Object.keys(payload).length === 0) { setSaving(false); return; }
    await onSave(section.id, payload);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="admin-cg-section-editor" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-4 border-b border-[#F4ECD8] flex items-center justify-between">
          <h3 className="font-display text-xl text-[#0A1F2E]">{editing.name} - section editor</h3>
          <button onClick={onClose} className="text-[#5A6B73] hover:text-[#0A1F2E]"><X className="w-5 h-5" /></button>
        </header>
        <div className="flex-1 overflow-hidden grid md:grid-cols-[200px_1fr]">
          <aside className="border-r border-[#F4ECD8] overflow-y-auto p-2 bg-[#F9F6EF]">
            {editing.sections.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`w-full text-left px-3 py-2 text-xs rounded mb-1 ${activeId === s.id ? 'bg-[#0A1F2E] text-white' : 'text-[#1A5C6E] hover:bg-[#F4ECD8]'}`}
                data-testid={`admin-cg-edit-tab-${s.id}`}
              >{s.title || s.id}</button>
            ))}
          </aside>
          <section className="overflow-y-auto p-5 space-y-3">
            {section?.intro !== undefined && (
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-[#1A5C6E] mb-1">Intro</label>
                <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={3} className="w-full text-sm border border-[#EFE6CF] rounded p-2 focus:outline-none focus:border-[#2CA8A0]" data-testid="admin-cg-edit-intro" />
              </div>
            )}
            {section?.narrative !== undefined && (
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-[#1A5C6E] mb-1">Narrative</label>
                <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={16} className="w-full text-sm border border-[#EFE6CF] rounded p-2 focus:outline-none focus:border-[#2CA8A0]" data-testid="admin-cg-edit-narrative" />
              </div>
            )}
            {section?.table && (
              <p className="text-xs text-[#5A6B73] italic">Tables and neighborhood rows are regenerate-only - use Regenerate to refresh structured rows.</p>
            )}
          </section>
        </div>
        <footer className="px-5 py-3 border-t border-[#F4ECD8] flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 text-[#1A5C6E] hover:text-[#0A1F2E]">Cancel</button>
          <button onClick={save} disabled={saving} className="text-sm px-4 py-2 bg-[#0A1F2E] text-white rounded hover:bg-[#1A5C6E] disabled:opacity-40" data-testid="admin-cg-edit-save">{saving ? 'Saving…' : 'Save changes'}</button>
        </footer>
      </div>
    </div>
  );
};

export default AdminCityGuidesPanel;
